// Angular
import { ChangeDetectionStrategy, Component, OnInit, TransferState, makeStateKey, inject, signal, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// Material
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
// Constants
import { COUNTRY_CURRENCIES, LOCALES, PART_LANG_HREF_REDIRECTS_PATHS } from '@constants';
// Utils
import { GlobalErrorHandler, HttpUtilsService } from '@core/utils';
import { environment } from '@environment';
// RxJs & Lodash
import { filter, find, get, includes } from 'lodash';
import { catchError } from 'rxjs';
// Service
import { LayoutService } from '../../services';
// Modules
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
// Pipe
import { SearchFilterPipe } from '@app/core/_base/layout';

// State key
const STATE_KEY_HREF = makeStateKey('hrefData');

@Component({
	selector: 'app-visitor-setting-dialog',
	templateUrl: './visitor-setting-dialog.component.html',
	styleUrls: ['./visitor-setting-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslocoDirective, MatDialogTitle, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, MatRadioGroup, MatRadioButton, MatFormField, MatLabel, MatSelect, MatSelectTrigger, MatOption, MatProgressSpinner, NgxMatSelectSearchModule, MatDialogActions, MatButton, MatDialogClose, TranslocoPipe, SearchFilterPipe]
})
export class VisitorSettingDialogComponent implements OnInit {

	// Private
	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly layoutService = inject(LayoutService);
	private readonly router = inject(Router);
	private readonly httpUtilsService = inject(HttpUtilsService);
	private readonly transferState = inject(TransferState);
	private readonly globalErrorHandler = inject(GlobalErrorHandler);

	// Public
	settingForm: FormGroup;
	loadingCountries = signal<boolean>(true);
	logisticCountries: Array<any> = [];
	searchCountryTxt: string;
	currentLanguage: string;
	loading = signal<boolean>(false);
	localeList = filter(LOCALES, (item) => includes(environment.visitorSettings.allowedLocale, item.locale));
	currencyList = filter(COUNTRY_CURRENCIES, (item) => includes(environment.visitorSettings.allowedCurrency, item.symbol));


	/**
	 * On Init
	 * @memberof VisitorSettingDialogComponent
	 */
	ngOnInit(): void {
		// Visitor settings form init
		this.initVisitorSettingForm();

		// Get Logisitic Countries
		this.httpUtilsService.getLogisticCountryList().pipe(
			catchError(error => this.globalErrorHandler.handleError('on-get-logistic-countries', error)),
			takeUntilDestroyed(this.destroyRef),
		).subscribe((response: any) => {
			this.logisticCountries = get(response, 'body', []);

			// Set customer type
			const customerType = this.layoutService.getCustomerType();
			this.settingForm.patchValue({
				customer_type: customerType
			});

			// Set country
			const currentCountry = this.layoutService.getCurrentCountry();
			const selectedCountry = find(this.logisticCountries, (i) => i.id === currentCountry?.id);
			this.settingForm.patchValue({
				country: selectedCountry
			});

			// Set language
			const currentLang = this.layoutService.getCustomerLanguage();
			const selectedLanguage = find(this.localeList, (i) => i.locale === currentLang);
			this.settingForm.patchValue({
				language: selectedLanguage
			});

			// Set currency
			const currency = this.layoutService.getCustomerCurrency();
			const selectedCurrency = find(this.currencyList, (i) => i.symbol === currency);
			this.settingForm.patchValue({
				currency: selectedCurrency
			});

			// Get current language
			this.currentLanguage = this.layoutService.getCustomerLanguage();
			this.loadingCountries.set(false);
		});
	}

	/**
	 * On Apply Setting
	 * @memberof VisitorSettingDialogComponent
	 */
	onApplySetting() {
		try {
			if (this.settingForm.invalid) return;

			this.loading.set(true);
			const controls = this.settingForm.value;

			const customerType = controls.customer_type;
			const customerCountry = controls.country;
			const customerCurrency = controls.currency;
			const customerLanguage = controls.language;

			// Set customer type, country, currency, and language
			this.layoutService.setCustomerType(customerType);
			this.layoutService.setCustomerCountry(customerCountry);
			this.layoutService.setCustomerCurrency(customerCurrency?.symbol);
			this.layoutService.setCustomerLanguage(customerLanguage);

			const result = this.transferState.get<any>(STATE_KEY_HREF, null);
			const currentURL = this.router.url;
			const isPartSearchPaths = PART_LANG_HREF_REDIRECTS_PATHS.some(path => currentURL.includes(path));
			if (customerLanguage?.locale !== this.currentLanguage) {
				if (isPartSearchPaths && result !== null) {
					// Only redirect for part-search routes
					window.location.href = result[customerLanguage?.id][2];
				} else {
					// Navigate to the home page and reload
					this.router.navigate([`/${customerLanguage?.locale}`]).then(() => {
						window.location.reload();
					});
				}
			} else {
				window.location.reload();
			}
		} catch (error) {
			this.globalErrorHandler.handleError('on-apply-visitor-setting', error)
		}
	}

	/**
	 * Initialize Visitor Setting Form 
	 * @private
	 * @memberof VisitorSettingDialogComponent
	 */
	private initVisitorSettingForm(): void {
		this.settingForm = this.fb.group({
			country: ['', Validators.required],
			language: ['', Validators.required],
			currency: ['', Validators.required],
			customer_type: ['', Validators.required],
		})
	}
}
